import type { UserLanguage } from '@xtreed/academy-shared';
import { api } from './api';

export interface EmailTemplate {
  id: string;
  slug: string;
  language: UserLanguage;
  name: string;
  subject: string;
  htmlContent: string;
  textContent: string | null;
  variables: string[];
  isActive: boolean;
  eventType?: string;
  description?: string;
  category?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmailPreview {
  subject: string;
  html: string;
  text?: string;
}

export interface EventTemplateLanguage {
  language: UserLanguage;
  id: string;
  name: string;
  isActive: boolean;
}

export interface EmailTemplateDefault {
  slug: string;
  language: UserLanguage;
  name: string;
  description: string;
  subject: string;
  htmlContent: string;
  variables: string[];
}

export interface EventCatalogEntry {
  eventType: string;
  category: string;
  templateSlug: string;
  variables: string[];
  template: {
    id: string;
    slug: string;
    name: string;
    isActive: boolean;
    languages: EventTemplateLanguage[];
  } | null;
}

export interface CreateEmailTemplateDto {
  slug: string;
  language: UserLanguage;
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  variables?: string[];
  eventType?: string;
  description?: string;
  category?: string;
  isActive?: boolean;
}

export interface CreateEmailTemplateLanguageDto {
  slug: string;
  language: UserLanguage;
  subject?: string;
  htmlContent?: string;
  textContent?: string;
}

class EmailTemplatesService {
  async findAll(): Promise<EmailTemplate[]> {
    const { data } = await api.get('/email-templates');
    return data?.data || data;
  }

  async findOne(id: string): Promise<EmailTemplate> {
    const { data } = await api.get(`/email-templates/${id}`);
    return data?.data || data;
  }

  async create(dto: CreateEmailTemplateDto): Promise<EmailTemplate> {
    const { data } = await api.post('/email-templates', dto);
    return data?.data || data;
  }

  async createLanguage(
    dto: CreateEmailTemplateLanguageDto,
  ): Promise<EmailTemplate> {
    const { data } = await api.post('/email-templates/languages', dto);
    return data?.data || data;
  }

  async update(
    id: string,
    dto: Partial<
      Pick<
        EmailTemplate,
        'subject' | 'htmlContent' | 'textContent' | 'isActive'
      >
    >,
  ): Promise<EmailTemplate> {
    const { data } = await api.put(`/email-templates/${id}`, dto);
    return data?.data || data;
  }

  async getDefault(
    slug: string,
    language: UserLanguage,
  ): Promise<EmailTemplateDefault> {
    const { data } = await api.get(`/email-templates/defaults/${slug}`, {
      params: { language },
    });
    return data?.data || data;
  }

  async resetToDefault(id: string): Promise<EmailTemplate> {
    const { data } = await api.post(`/email-templates/${id}/reset`);
    return data?.data || data;
  }

  async preview(
    slug: string,
    variables?: Record<string, string>,
    language?: UserLanguage,
  ): Promise<EmailPreview> {
    const { data } = await api.post('/email-templates/preview', {
      slug,
      variables,
      language,
    });
    return data?.data || data;
  }

  async getEventCatalog(): Promise<EventCatalogEntry[]> {
    const { data } = await api.get('/email-templates/events');
    return data?.data || data || [];
  }
}

export const emailTemplatesService = new EmailTemplatesService();
